import React from "react"
import { useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { Heart, Play } from "lucide-react"
import styles from "./FeedPreviewPanel.module.scss"
import { useDashboardFeedPreview } from "./useDashboardFeedPreview.ts"
import { openFeedTextModal } from "../../../../store/actions"
import type { FeedPreviewCardModel } from "../../../../utils/feedDashboardPreview.ts"

interface FeedPreviewPanelProps {
  active: boolean
}

const SKELETON_SLOTS = [0, 1, 2]

const FeedPreviewCard: React.FC<{
  card: FeedPreviewCardModel
  onOpenProfile: (url: string) => void
  onExploreAll: () => void
  onPublish: () => void
}> = ({ card, onOpenProfile, onExploreAll, onPublish }) => (
  <article className={styles.previewCard}>
    <button
      type="button"
      className={styles.cardMain}
      onClick={() => onOpenProfile(card.profileUrl)}
      aria-label={`Ver ${card.badge} de ${card.authorName}`}
    >
      <div className={styles.media}>
        {card.mediaKind === "video" ? (
          <>
            {card.posterUrl ? (
              <img src={card.posterUrl} alt="" className={styles.mediaImage} />
            ) : (
              <video
                src={card.mediaUrl}
                className={styles.mediaVideo}
                muted
                playsInline
                preload="metadata"
              />
            )}
            <span className={styles.playBadge} aria-hidden>
              <span className={styles.playIcon}>
                <Play size={14} fill="currentColor" />
              </span>
            </span>
          </>
        ) : (
          <img src={card.mediaUrl} alt="" className={styles.mediaImage} />
        )}
        {card.likesCount != null && card.likesCount > 0 ? (
          <span className={styles.likesPill} aria-label={`${card.likesCount} me gusta`}>
            <Heart size={10} fill="currentColor" aria-hidden />
            <span>{card.likesCount}</span>
          </span>
        ) : null}
      </div>
      <div className={styles.body}>
        <span className={styles.typeBadge}>{card.badge}</span>
        <p className={styles.title}>{card.title}</p>
        <span className={styles.author}>{card.authorName}</span>
      </div>
    </button>
    <div className={styles.cardOverlay}>
      <button
        type="button"
        className={styles.overlayBtnPrimary}
        onClick={(e) => {
          e.stopPropagation()
          onExploreAll()
        }}
      >
        Ver todos
      </button>
      <button
        type="button"
        className={styles.overlayBtnSecondary}
        onClick={(e) => {
          e.stopPropagation()
          onPublish()
        }}
      >
        Publicar
      </button>
    </div>
  </article>
)

const FeedPreviewPanel: React.FC<FeedPreviewPanelProps> = ({ active }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { cards, loading, error } = useDashboardFeedPreview(active)

  const handleOpenProfile = (url: string) => {
    navigate(url)
  }

  const handleExploreAll = () => {
    navigate("/marketplace/explorar")
  }

  const handlePublish = () => {
    dispatch(openFeedTextModal({ text: true }))
  }

  return (
    <div className={styles.panel}>
      {loading ? (
        <div className={styles.previewGrid}>
          {SKELETON_SLOTS.map((slot) => (
            <div key={slot} className={styles.skeletonCard} aria-hidden>
              <div className={styles.skeletonMedia} />
              <div className={styles.skeletonBody}>
                <div className={styles.skeletonBadge} />
                <div className={styles.skeletonLine} />
                <div className={styles.skeletonLine} />
                <div className={styles.skeletonLine} />
              </div>
            </div>
          ))}
        </div>
      ) : cards.length > 0 ? (
        <div className={styles.previewGrid}>
          {cards.map((card) => (
            <FeedPreviewCard
              key={card.key}
              card={card}
              onOpenProfile={handleOpenProfile}
              onExploreAll={handleExploreAll}
              onPublish={handlePublish}
            />
          ))}
        </div>
      ) : (
        <p className={styles.empty}>
          {error
            ? "No pudimos cargar las novedades. Probá de nuevo más tarde."
            : "Todavía no hay publicaciones con imagen o video para mostrar acá."}
        </p>
      )}
    </div>
  )
}

export default FeedPreviewPanel

